import { Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import EditableTextField from 'components/editableTextField';
import Papa from 'papaparse';
import React, { useMemo, useState } from 'react';
import { FinsightEntity, FinsightEntityField } from 'shared';
import { sortFields } from 'utils/fields';

export default function TextView({
  entities,
}: {
  entities?: FinsightEntity[];
}) {
  // State to track which columns are selected
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(
    new Set(),
  );

  // State to track manual CSV text input
  const [manualCsvText, setManualCsvText] = useState<string>('');

  // Get all available columns
  const availableColumns = useMemo(() => {
    if (!entities || entities.length === 0) {
      return [];
    }

    // Get all unique field names from all entities
    const allFieldNames = new Set<string>();
    entities.forEach((entity) => {
      Object.keys(entity.extraFieldsMeta).forEach((fieldName) => {
        allFieldNames.add(fieldName);
      });
    });

    // Sort fields using the same logic as the create modal
    const sortedFieldNames = sortFields(
      Array.from(allFieldNames)
        .map((fieldName) => {
          // Find the first entity that has this field to get the field metadata
          const firstEntityWithField = entities.find(
            (entity) => entity.extraFieldsMeta[fieldName],
          );
          return firstEntityWithField?.extraFieldsMeta[fieldName];
        })
        .filter((field): field is FinsightEntityField => field !== undefined),
    ).map((field) => field.name);

    // Return base columns + sorted extra field names
    return [
      'name',
      'description',
      ...sortedFieldNames.filter((fieldName) => fieldName !== 'order'),
    ];
  }, [entities]);

  // Parse CSV headers from manual text input
  const parseCsvHeaders = (csvText: string): string[] => {
    try {
      const lines = csvText.trim().split('\n');
      if (lines.length === 0) return [];

      const firstLine = lines[0];
      const result = Papa.parse(firstLine, { header: false });

      if (result.data && result.data.length > 0) {
        return (result.data[0] as string[]) || [];
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  // Update selected columns based on CSV headers
  const updateSelectedColumnsFromCsv = (csvText: string) => {
    const headers = parseCsvHeaders(csvText);
    const newSelectedColumns = new Set<string>();

    headers.forEach((header) => {
      if (availableColumns.includes(header)) {
        newSelectedColumns.add(header);
      }
    });

    setSelectedColumns(newSelectedColumns);
  };

  // Initialize selected columns on first load
  React.useEffect(() => {
    if (availableColumns.length > 0 && selectedColumns.size === 0) {
      setSelectedColumns(new Set(availableColumns));
    }
  }, [availableColumns, selectedColumns.size]);

  // Convert entities to CSV format using the same logic as the create modal
  const csvValue = useMemo(() => {
    if (!entities || entities.length === 0) {
      return '';
    }

    // Convert entities to CSV data
    const csvData = entities.map((entity) => {
      const data: Record<string, string> = {};

      // Add selected columns
      availableColumns.forEach((columnName) => {
        if (selectedColumns.has(columnName)) {
          if (columnName === 'name') {
            data[columnName] = entity.name || '';
          } else if (columnName === 'description') {
            data[columnName] = entity.description || '';
          } else if (columnName === 'type') {
            data[columnName] = entity.types[0] || '';
          } else {
            // Extra field
            const value = entity.extraFields[columnName];
            data[columnName] =
              value !== undefined && value !== null ? String(value) : '';
          }
        }
      });

      return data;
    });

    // Convert to CSV string using Papa.parse
    return Papa.unparse(csvData);
  }, [entities, availableColumns, selectedColumns]);

  // Update manual CSV text when auto-generated CSV changes
  React.useEffect(() => {
    setManualCsvText(csvValue);
  }, [csvValue]);

  const handleColumnToggle = (columnName: string) => {
    const newSelectedColumns = new Set(selectedColumns);
    if (newSelectedColumns.has(columnName)) {
      newSelectedColumns.delete(columnName);
    } else {
      newSelectedColumns.add(columnName);
    }
    setSelectedColumns(newSelectedColumns);
  };

  const handleCsvTextChange = async (newText: string) => {
    setManualCsvText(newText);
    updateSelectedColumnsFromCsv(newText);
  };

  return (
    <Stack gap={2}>
      <Stack>
        <Typography variant="subtitle2" gutterBottom>
          Select columns to include:
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {availableColumns.map((columnName) => (
            <FormControlLabel
              key={columnName}
              control={
                <Checkbox
                  checked={selectedColumns.has(columnName)}
                  onChange={() => handleColumnToggle(columnName)}
                  size="small"
                />
              }
              label={columnName}
            />
          ))}
        </Stack>
      </Stack>
      <EditableTextField
        variant="outlined"
        value={manualCsvText}
        onChange={handleCsvTextChange}
        multiline
        minRows={10}
        fullWidth
      />
    </Stack>
  );
}
